import {
  Box,
  Dialog,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { CommonOutlinedButton } from "../../common/StyledComponents/StyledFields";
import styles from "../../devoteeService/priestSchedule/priestSchedule.module.scss";
import appStyles from "../../../App.module.scss";
import { displayDateFormat } from "../../../infrastructure/backendService";
import { Close } from "@mui/icons-material";
import PageLoader from "../../common/PageLoader/PageLoader";
import { useReactToPrint } from "react-to-print";
import { printStyles } from "../PriestDailySchedulesReport/PriestDailySchedulesReport";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import DraggableFunction from "../../common/DraggableDialogFunction/DraggableFunction";
import ButtonLoader from "../../common/ButtonLoader/ButtonLoader";

interface weeklyScheduleIProps {
  handleClose: () => void;
  open: boolean;
  isPageLoading: boolean;
  schedulePeriod: { startDate: any; endDate: any };
  priestsWeeklySchedule: any[];
  dateAndDays: { date: string; day: string }[];
  handlePreviousAndNextWeek: (isPrevious?: boolean) => void;
  isViewPriestSchedule?: boolean;
  isHallSchedule?: boolean;
  handleExportToExcel: () => void;
  isExcelLoading: boolean;
}

const WeeklyScheduleDialog = (props: weeklyScheduleIProps) => {
  const {
    handleClose,
    open,
    isPageLoading,
    schedulePeriod,
    priestsWeeklySchedule,
    dateAndDays,
    handlePreviousAndNextWeek,
    isViewPriestSchedule,
    isHallSchedule,
    handleExportToExcel,
    isExcelLoading,
  } = props;

  const componentRef = useRef(null);
  const [fullScreenView, setFullScreenView] = useState(true);

  useEffect(() => {
    setFullScreenView(true);
  }, [isHallSchedule, isViewPriestSchedule]);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: printStyles,
    documentTitle: `${
      !isHallSchedule ? "Priest" : "Hall"
    } weekly report ${displayDateFormat(
      new Date(schedulePeriod?.startDate)
    )} -  ${displayDateFormat(new Date(schedulePeriod?.endDate))}`,
  });

  const handleFullScreen = () => {
    setFullScreenView(!fullScreenView);
  };

  const getDialogWidthAndHeight = () => {
    let dialogStyles: any = null;
    if (fullScreenView) {
      dialogStyles = {
        width: "100vw",
        height: "100vh",
        maxWidth: "100vw",
        maxHeight: "100vh",
        borderRadius: "0px",
        margin: "0px",
        transform: "none !important",
        background: "#fff9e4",
        top: "0px",
        left: "0px",
        bottom: "0px",
        right: "0px",
        position: "fixed",
      };
    } else {
      dialogStyles = {
        width: { xs: "100%", md: "90%" },
        maxWidth: { xs: "100%", md: "1200px" },
        maxHeight: {
          xs: "calc(100% - 30px)",
          sm: "calc(100% - 40px)",
        },
        borderRadius: "8px",
        margin: { xs: "15px", sm: "20px" },
      };
    }
    return dialogStyles;
  };

  const renderWeekAndDayButtons = () => {
    return (
      <Box
        px="15px"
        pt="10px"
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: "10px",
          justifyContent: "space-between",
        }}
        className="no_print"
      >
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
          <CommonOutlinedButton
            className={styles.week_and_day_buttons}
            onClick={() => {
              handlePreviousAndNextWeek(true);
            }}
            sx={{
              fontSize: { xs: "12px !important", sm: "15px !important" },
              height: "fit-content !important",
              p: { xs: "5px !important", sm: "8px !important" },
            }}
          >
            {"< Previous Week >"}
          </CommonOutlinedButton>
          <CommonOutlinedButton
            className={styles.week_and_day_buttons}
            onClick={() => handlePreviousAndNextWeek()}
            sx={{
              fontSize: { xs: "12px !important", sm: "15px !important" },
              height: "fit-content !important",
              p: { xs: "5px !important", sm: "8px !important" },
            }}
          >
            {"< Next Week >"}
          </CommonOutlinedButton>
        </Box>
        <Box>
          <CommonOutlinedButton
            className={styles.week_and_day_buttons}
            onClick={handlePrint}
            sx={{
              fontSize: { xs: "12px !important", sm: "15px !important" },
              height: "fit-content !important",
              p: { xs: "5px !important", sm: "8px !important" },
              mr: "10px",
            }}
          >
            Print Report
          </CommonOutlinedButton>
          <CommonOutlinedButton
            className={styles.week_and_day_buttons}
            onClick={handleExportToExcel}
            sx={{
              fontSize: { xs: "12px !important", sm: "15px !important" },
              height: "fit-content !important",
              p: { xs: "5px !important", sm: "8px !important" },
            }}
          >
            <ButtonLoader
              isBtnLoading={isExcelLoading}
              btnText="Export to Excel"
            />
          </CommonOutlinedButton>
        </Box>
      </Box>
    );
  };

  const renderSchedulesTable = () => {
    return (
      <Box
        className="print_width_height"
        sx={{
          my: "10px",
          mx: "15px",
          overflowX: "auto",
          "&::-webkit-scrollbar": {
            width: "8px",
            height: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#F2BE22", // Scrollbar color
            borderRadius: "8px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "white",
          },
        }}
      >
        <Box
          className="print_width_height"
          sx={{
            maxHeight: `${fullScreenView ? "auto" : "calc(100vh - 250px)"}`,
            minHeight: "auto",
            overflow: "auto",
            position: "relative",
            minWidth: { xs: "800px", md: "1000px" },
            "&::-webkit-scrollbar": {
              width: "8px",
              height: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#F2BE22", // Scrollbar color
              borderRadius: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "white",
            },
          }}
        >
          <table
            className={`${appStyles.table_common_container} ${appStyles.table_layout_fix}`}
            style={{ position: "relative" }}
          >
            <thead style={{ position: "sticky", top: "0px" }}>
              <th className={appStyles?.table_heading_1}>
                {isHallSchedule ? "Hall" : "Priest"} Name
              </th>
              {dateAndDays?.map((each: any) => (
                <th className={appStyles?.table_heading_1} key={each?.date}>
                  {each?.day} <br /> ({each?.date})
                </th>
              ))}
            </thead>
            <tbody>
              {priestsWeeklySchedule?.map((schedules: any, index: number) => {
                return (
                  <tr key={index}>
                    <td
                      style={{ fontSize: "12px", width: "75px" }}
                      className={appStyles.txt_bold}
                    >
                      {schedules?.priestOrHallName}
                    </td>
                    {schedules?.schedulesByDate?.map(
                      (schedulesForDay: any, index: number) => {
                        return (
                          <td key={`${schedulesForDay?.date}_${index}`}>
                            {schedulesForDay?.schedules?.length > 0 ? (
                              <>
                                {schedulesForDay?.schedules?.map(
                                  (schedule: any, ind: number) => {
                                    return (
                                      <Box
                                        key={schedule?.startTime}
                                        sx={{
                                          mb: `${
                                            schedulesForDay?.schedules
                                              ?.length !==
                                            ind + 1
                                              ? "5px"
                                              : "0px"
                                          }`,
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            cursor: "initial !important",
                                            textDecoration: "none !important",
                                            fontWeight: "bold !important",
                                            fontSize: "12px !important",
                                            margin: "0px !important",
                                            fontFamily: "system-ui !important",
                                          }}
                                        >
                                          {schedule?.startTime} -{" "}
                                          {schedule?.endTime}:
                                        </Typography>
                                        <Typography
                                          sx={{
                                            cursor: "initial !important",
                                            textDecoration: "none !important",
                                            fontSize: "12px !important",
                                            margin: "0px !important",
                                          }}
                                        >
                                          {isViewPriestSchedule ? (
                                            <>{schedule?.description}</>
                                          ) : (
                                            <>
                                              {schedule?.devoteeDetails
                                                ? schedule?.outsidePooja
                                                  ? "Private pooja"
                                                  : "Temple pooja"
                                                : "Priest duties"}
                                            </>
                                          )}
                                        </Typography>
                                      </Box>
                                    );
                                  }
                                )}
                              </>
                            ) : (
                              "NA"
                            )}
                          </td>
                        );
                      }
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Box>
      </Box>
    );
  };

  const renderButtonsComponent = () => {
    return (
      <Box
        sx={{ px: "15px", pb: "15px", display: { xs: "none", sm: "block" } }}
        className="no_print"
      >
        <CommonOutlinedButton
          onClick={() => {
            handleClose();
            setFullScreenView(true);
          }}
        >
          Cancel
        </CommonOutlinedButton>
      </Box>
    );
  };

  return (
    <Dialog
      onClose={handleClose}
      open={open}
      PaperProps={{
        sx: { ...getDialogWidthAndHeight() },
      }}
      PaperComponent={DraggableFunction}
      aria-labelledby="draggable-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <Box
        className={styles.scheduling_container}
        ref={componentRef}
        sx={{ border: "none !important" }}
      >
        <Box
          className={styles.scheduling_title_container}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            cursor: "move",
            p: "15px !important",
            alignItems: "center",
          }}
        >
          <DialogTitle
            className={styles.scheduling_title}
            fontSize={{ xs: "15px", md: "18px" }}
            sx={{ p: "0px !important", flexGrow: 1 }}
          >
            {`${!isHallSchedule ? "Priest" : "Hall"} Weekly Report`}{" "}
            <Box component="span" display={{ xs: "none", sm: "inline" }}>
              {`(Period ${displayDateFormat(
                schedulePeriod?.startDate
              )} to ${displayDateFormat(schedulePeriod?.endDate)})`}
            </Box>
          </DialogTitle>
          <Box
            className="no_print"
            sx={{ display: "flex", alignItems: "center" }}
          >
            <IconButton
              onClick={handleFullScreen}
              sx={{ p: "0px", zIndex: 1001 }}
            >
              {!fullScreenView ? (
                <FullscreenIcon sx={{ color: "white", cursor: "pointer" }} />
              ) : (
                <FullscreenExitIcon
                  sx={{ color: "white", cursor: "pointer" }}
                />
              )}
            </IconButton>
            <IconButton onClick={handleClose} sx={{ p: "0px" }}>
              <Close sx={{ color: "white", cursor: "pointer", ml: "10px" }} />
            </IconButton>
          </Box>
        </Box>
        {renderWeekAndDayButtons()}
        {renderSchedulesTable()}
        {renderButtonsComponent()}
      </Box>
      {(isPageLoading || isExcelLoading) && (
        <PageLoader loading={isPageLoading || isExcelLoading} />
      )}
    </Dialog>
  );
};

export default WeeklyScheduleDialog;
